<template>
    <div class="h-auto lg:h-full">
        <template v-if="!hasFinished">
            <div
                v-if="!hasStarted"
                class="relative py-20 flex justify-center px-5 md:px-3 xl:px-0 md:py-10 bg-center bg-repeat bg-cover h-full"
                style="background-image: url('assets/top-background.svg')"
            >
                <div class="container relative z-10 xl:mt-8">
                    <div class="w-full xl:w-10/12 mx-auto">
                        <section class="bg-white border border-gray-300 shadow p-5 xl:p-20">
                            <h2
                                class="font-heading uppercase text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-center text-orange mb-3"
                                v-text="introductionPageData[0]?.page_title"
                            ></h2>
                            <div class="flex justify-center mb-5">
                                <p class="prose text-center" v-text="introductionPageData[0]?.text"></p>
                            </div>
                            <div class="flex justify-center mb-10">
                                <button
                                    class="px-5 py-2 flex items-center border-b border-orange-600 bg-orange text-white flex-grow-0 space-x-3 font-bold hover:bg-orange-600 hover:border-orange transition duration-300 ease-in-out active:ring-2 active:ring-orange-600 focus:ring-2 focus:ring-orange-600"
                                    @click="hasStarted = !hasStarted"
                                >
                                    <span>Start de test</span>
                                    <i class="fa-regular fa-chevron-right"></i>
                                </button>
                            </div>
                            <div v-if="introductionPageData[0]?.image" class="flex justify-center items-center">
                                <div class="h-auto overflow-hidden w-52 md:w-64 xl:w-80 aspect-square">
                                    <img
                                        class="w-full h-full object-cover"
                                        :src="introductionPageData[0]?.image.url"
                                        :alt="introductionPageData[0]?.page_title"
                                    />
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            <div
                v-else
                class="relative py-20 flex justify-center px-5 md:px-3 xl:px-0 md:py-10 bg-center bg-repeat bg-cover h-full"
                style="background-image: url('assets/top-background.svg')"
            >
                <div class="container relative z-10 xl:mt-8">
                    <div class="w-full xl:w-10/12 mx-auto">
                        <section class="bg-white border border-gray-300 shadow px-5 xl:px-20">
                            <div class="py-8 lg:py-5 xl:py-16">
                                <div class="grid grid-cols-8 gap-5">
                                    <div class="col-span-8">
                                        <div class="bg-white border border-gray-300 shadow py-5 px-3 lg:py-10 lg:px-5">
                                            <span v-text="currentPropertyQuestion.question_text"></span>
                                        </div>
                                    </div>
                                    <div class="col-span-8 md:col-span-4">
                                        <section class="flex flex-col space-y-4 xl:space-y-8">
                                            <button
                                                v-for="(answer, answerIndex) in currentPropertyQuestion.answers"
                                                :key="answerIndex"
                                                class="group flex items-center focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                                                @click="handleAnswerClick(answer)"
                                            >
                                                <div
                                                    class="rounded-full flex items-center justify-center transition duration-300 ease-in-out font-bold text-white font-heading uppercase aspect-square h-20 z-10 text-lg"
                                                    :class="answerButtonColorClasses[answerIndex]"
                                                    v-text="answerIndex + 1"
                                                ></div>

                                                <div
                                                    class="bg-white border border-gray-300 shadow flex-grow-0 pl-8 pr-3 lg:pl-10 lg:pr-5 w-full text-left h-auto py-1 min flex items-center z-0 -ml-5 min-h-[4rem]"
                                                >
                                                    <span class="text-xs lg:text-sm" v-text="answer.answer_text" />
                                                </div>
                                            </button>
                                        </section>
                                    </div>
                                    <div
                                        v-if="currentPropertyQuestion.image"
                                        class="hidden col-span-4 md:flex justify-center items-center"
                                    >
                                        <div class="h-auto overflow-hidden w-52 md:w-64 xl:w-80 aspect-video">
                                            <img
                                                class="w-full h-full object-cover"
                                                :src="currentPropertyQuestion.image.url"
                                                :alt="currentPropertyQuestion.question_text"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <a
                            href="/"
                            class="flex items-center space-x-1 hover:underline mt-2 focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                        >
                            <i class="fa-regular fa-chevron-left"></i>

                            <span v-text="snippets.match_test_backlink"></span>
                        </a>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div
                class="relative py-20 flex justify-center px-5 md:px-3 xl:px-0 md:py-10 bg-center bg-repeat bg-cover h-full"
                style="background-image: url('assets/top-background.svg')"
            >
                <div class="container relative z-10 xl:mt-8">
                    <div class="max-w-md mx-auto">
                        <section class="flex bg-white border border-gray-300 shadow px-5 xl:px-20">
                            <div class="flex flex-col m-auto py-4 px-6">
                                <i class="fa-regular fa-spinner-third text-4xl text-teal mx-auto mb-4 animate-spin"></i>
                                <p>Je score wordt berekend...</p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { ref, inject } from "vue";
// eslint-disable-next-line import/extensions

const snippets = inject("statamic_snippets");

const props = defineProps({
    matchTestData: {
        type: Array,
        required: true,
    },
    matchTestPositiveUrl: {
        type: String,
        required: true,
    },
    matchTestNegativeUrl: {
        type: String,
        required: true,
    },
    introductionPageData: {
        type: Array,
        required: true,
    },
});

const hasStarted = ref(false);
const hasFailed = ref(false);
const hasFinished = ref(false);

const currentEmployeePropertyIndex = ref(0);
const currentEmployeeProperty = ref(props.matchTestData[currentEmployeePropertyIndex.value]);

const currentPropertyQuestionIndex = ref(0);
const currentPropertyQuestion = ref(currentEmployeeProperty.value.questions[currentPropertyQuestionIndex.value]);

const answerButtonColorClasses = ref([
    "border-blue bg-blue group-hover:border-blue-600 group-hover:bg-blue-600",
    "border-teal bg-teal group-hover:border-teal-600 group-hover:bg-teal-600",
    "border-orange bg-orange group-hover:border-orange-600 group-hover:bg-orange-600",
    "border-blue bg-blue group-hover:border-blue-600 group-hover:bg-blue-600",
]);

const updateCurrentQuestion = () => {
    currentEmployeeProperty.value = props.matchTestData[currentEmployeePropertyIndex.value];
    currentPropertyQuestion.value = currentEmployeeProperty.value.questions[currentPropertyQuestionIndex.value];
};

const finishMatchTest = () => {
    hasFinished.value = true;

    // Redirect the user to the application form
    window.location.href = hasFailed.value ? props.matchTestNegativeUrl : props.matchTestPositiveUrl;
};

const handleAnswerCorrect = () => {
    if (!(currentEmployeePropertyIndex.value + 1 in props.matchTestData)) {
        // There are no more employee properties left, mark as finished
        finishMatchTest();

        return;
    }

    // Show the first question in the next employee property
    currentEmployeePropertyIndex.value += 1;
    currentPropertyQuestionIndex.value = 0;
    updateCurrentQuestion();
};

const handleAnswerWrong = () => {
    if (!(currentPropertyQuestionIndex.value + 1 in currentEmployeeProperty.value.questions)) {
        // There are no more questions in this employee property, mark as incompatible
        hasFailed.value = true;

        // todo: Think of a better name that also fits this call?
        handleAnswerCorrect();

        return;
    }

    // Show the next question in this employee property
    currentPropertyQuestionIndex.value += 1;
    updateCurrentQuestion();
};

const handleAnswerClick = (answer) => {
    if (answer.is_correct_answer) {
        handleAnswerCorrect();

        return;
    }

    handleAnswerWrong();
};
</script>
